/**
 * useShopWatcher — Surveillance intelligente du statut magasin
 *
 * Situations gérées :
 *  1. Sortie de zone GPS (> 100m)
 *  2. Horaire de fermeture dépassé
 *  3. GPS perdu ou refusé
 *  4. Déconnexion réseau
 *  5. App fermée / onglet en arrière-plan (onDisconnect Firebase)
 *  6. Magasin ouvert depuis très longtemps sans interaction (> 12h)
 *
 * Stratégie onDisconnect :
 *  - À chaque position GPS confirmée "inside", on écrit lastSeenAt + on arme
 *    un onDisconnect qui passera le shop en "uncertain" si la connexion coupe.
 *  - Le timer JS (5 min) reste en place pour les sorties de zone normales.
 *  - Les deux mécanismes se complètent : le timer gère la sortie douce,
 *    onDisconnect gère la fermeture brutale de l'app.
 */

import { useEffect, useRef, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, onDisconnect, serverTimestamp as rtdbTimestamp, set, remove } from 'firebase/database';
import { db, rtdb } from '../firebase/config';
import { useLocation } from '../contexts/LocationContext';
import { useNotifications } from './useNotifications';

/* ─── Constantes ──────────────────────────────────────────── */
const GEOFENCE_RADIUS      = 0.05;              // 50m (bon compromis précision GPS / réalité)
const EXIT_UNCERTAIN_DELAY = 15 * 60 * 1000;   // 15 min après sortie → incertain
const GPS_LOST_DELAY       = 30 * 60 * 1000;   // 30 min sans GPS → incertain
const OFFLINE_DELAY        = 45 * 60 * 1000;   // 45 min hors ligne → incertain
const LONG_OPEN_HOURS      = 12 * 60 * 60 * 1000; // 12h ouvert sans action → rappel

/* ─── Helpers Firebase Realtime Database (onDisconnect) ───── */

/**
 * Arme un onDisconnect sur le nœud RTDB du shop.
 * Quand la connexion coupe (app fermée, crash, perte réseau),
 * Firebase écrit automatiquement côté serveur :
 *   shops/<shopId>/disconnectedAt  → timestamp serveur
 *   shops/<shopId>/status          → "uncertain"
 *
 * Une Cloud Function (ou Firestore trigger) peut surveiller ce nœud,
 * mais on s'appuie ici sur la règle RTDB + une écriture miroir Firestore
 * via un second onDisconnect sur le document Firestore (émulé via RTDB
 * car Firestore ne supporte pas nativement onDisconnect).
 *
 * Approche retenue : on écrit dans RTDB un nœud "presence/<shopId>"
 * avec onDisconnect → { status: 'uncertain', disconnectedAt: timestamp }.
 * Le useShopWatcher écoute ce nœud et répercute dans Firestore.
 */
const armDisconnect = async (shopId) => {
  if (!rtdb || !shopId) return null;
  try {
    const presenceRef = ref(rtdb, `presence/${shopId}`);

    // Écrire "online" tant que connecté
    await set(presenceRef, {
      status:      'online',
      lastSeenAt:  rtdbTimestamp(),
    });

    // Quand la connexion coupe → Firebase écrit automatiquement
    await onDisconnect(presenceRef).set({
      status:         'uncertain',
      disconnectedAt: rtdbTimestamp(),
    });

    return presenceRef;
  } catch (err) {
    console.warn('useShopWatcher: onDisconnect non disponible', err);
    return null;
  }
};

const disarmDisconnect = async (shopId) => {
  if (!rtdb || !shopId) return;
  try {
    const presenceRef = ref(rtdb, `presence/${shopId}`);
    await onDisconnect(presenceRef).cancel();
    await remove(presenceRef);
  } catch (err) {
    console.warn('useShopWatcher: erreur désarmement onDisconnect', err);
  }
};

/* ─────────────────────────────────────────────────────────── */


const parseOpenHours = (str) => {
  if (!str) return null;
  const map = {};
  const daysList = [
    { key: 'lun', idx: 1 }, { key: 'mar', idx: 2 }, { key: 'mer', idx: 3 },
    { key: 'jeu', idx: 4 }, { key: 'ven', idx: 5 }, { key: 'sam', idx: 6 },
    { key: 'dim', idx: 0 },
  ];

  str.split('\n').forEach(line => {
    const m = line.match(/^(\w+(?:-\w+)?)\s*:\s*(.+)$/i);
    if (!m) return;
    const closed = /ferm/i.test(m[2]);
    const t = m[2].match(/(\d+)h(\d*)\s*[-–]\s*(\d+)h(\d*)/);
    const start = t ? parseInt(t[1]) * 60 + (parseInt(t[2]) || 0) : 0;
    const end   = t ? parseInt(t[3]) * 60 + (parseInt(t[4]) || 0) : 0;
    const range = m[1].split('-').map(x => x.toLowerCase().slice(0, 3));
    const from  = daysList.findIndex(d => d.key === range[0]);
    const to    = range[1] ? daysList.findIndex(d => d.key === range[1]) : from;
    if (from !== -1) {
      for (let i = from; i <= (to === -1 ? from : to); i++) {
        map[daysList[i].idx] = { open: !closed, start, end };
      }
    }
  });
  return map;
};

/* Heure actuelle en minutes depuis minuit */
const nowMinutes = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
};

/* ─── Minuteries actives (une seule instance par type) ───── */
/* NOTE: ces helpers agissent sur un Map passé en paramètre    */
/* afin d'éviter des collisions entre instances ou remontages  */
const setTimer = (map, key, fn, delay) => {
  clearTimeout(map.get(key));
  map.set(key, setTimeout(fn, delay));
};
const clearTimer = (map, key) => {
  clearTimeout(map.get(key));
  map.delete(key);
};
const clearAllTimers = (map) => {
  map.forEach((id) => clearTimeout(id));
  map.clear();
};

/* ═══════════════════════════════════════════════════════════ */
export const useShopWatcher = (shop, onStatusChange) => {
  const { startWatching, calculateDistance } = useLocation();
  const { sendNotification }                 = useNotifications();

  const isInsideRef     = useRef(null);
  const stopWatchRef    = useRef(null);
  const scheduleRef     = useRef(null);
  const shopRef         = useRef(shop);
  const presenceRef     = useRef(null);

  /* Fix #9 : timers locaux à l'instance — plus de variable module-level */
  const timersRef         = useRef(new Map());
  /* Fix #10 : lastHeartbeat local à l'instance */
  const lastHeartbeatRef = useRef(0);

  /* Toujours à jour dans les callbacks sans les mettre en dep */
  useEffect(() => { shopRef.current = shop; }, [shop]);

  /* Heartbeat : écrit lastSeenAt dans Firestore toutes les 90s */
  const writeHeartbeat = useCallback(async (shopId) => {
    const now = Date.now();
    if (now - lastHeartbeatRef.current < 90_000) return;
    lastHeartbeatRef.current = now;
    try {
      await updateDoc(doc(db, 'shops', shopId), { lastSeenAt: serverTimestamp() });
    } catch (err) {
      console.warn('useShopWatcher: heartbeat échoué', err);
    }
  }, []);

  /* ── Action centrale : changer le statut + notifier ─────── */
  const changeStatus = useCallback(async (newStatus, notifTitle, notifBody) => {
    const s = shopRef.current;
    if (!s?.id || s.status === newStatus) return;

    try {
      await updateDoc(doc(db, 'shops', s.id), {
        status:    newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: 'auto',
      });
      /* NE PAS appeler onStatusChange ici — il ferait une double écriture Firestore.
         L'onSnapshot du Dashboard détectera le changement automatiquement. */
      if (notifTitle) {
        sendNotification(notifTitle, {
          body: notifBody,
          tag:  `yeldi-${newStatus}`,
          requireInteraction: false,
        });
      }
    } catch (err) {
      console.error('useShopWatcher: erreur mise à jour statut', err);
    }
  }, [sendNotification]);

  /* ── Notification simple (rappel doux) ──────────────────── */
  const remind = useCallback((title, body, tag = 'yeldi-reminder') => {
    sendNotification(title, { body, tag, requireInteraction: false });
  }, [sendNotification]);

  /* ── Notification avec actions ──────────────────────────── */
  const ask = useCallback((title, body, tag, actions, shopId) => {
    sendNotification(title, { body, tag, requireInteraction: true, actions, data: { shopId } });
  }, [sendNotification]);

  /* ══════════════════════════════════════════════════════════
     SITUATION 1 — Sortie de zone GPS
     ══════════════════════════════════════════════════════════ */
  const handlePosition = useCallback((loc) => {
    const s = shopRef.current;
    if (!s?.latitude || !s?.longitude || s.status !== 'open') return;

    const distance  = calculateDistance(loc.lat, loc.lng, parseFloat(s.latitude), parseFloat(s.longitude));
    const isInside  = distance <= GEOFENCE_RADIUS;
    const wasInside = isInsideRef.current;

    /* Première position connue → initialiser et agir si déjà hors zone */
    if (wasInside === null) {
      isInsideRef.current = isInside;
      /* Si le commerçant démarre l'app déjà hors de sa zone → lancer le timer */
      if (!isInside) {
        setTimer(timersRef.current, 'exit', () => {
          changeStatus(
            'uncertain',
            '🟡 Statut mis en attente',
            'Vous n\'êtes pas dans votre magasin. Statut passé en incertain.'
          );
        }, EXIT_UNCERTAIN_DELAY);
      }
      return;
    }

    /* Vient de sortir */
    if (wasInside && !isInside) {
      ask(
        '📍 Vous avez quitté votre magasin',
        'Souhaitez-vous le marquer comme fermé ?',
        'geofence-exit',
        [
          { action: 'close-shop', title: 'Oui, fermer' },
          { action: 'keep-open',  title: 'Non, laisser ouvert' },
        ],
        s.id
      );

      /* Aucune réponse après 5 min → incertain */
      setTimer(timersRef.current, 'exit', () => {
        changeStatus(
          'uncertain',
          '🟡 Statut mis en attente',
          'Votre magasin est affiché comme "statut incertain" pour vos clients. Ouvrez Yeldi pour confirmer.'
        );
      }, EXIT_UNCERTAIN_DELAY);
    }

    /* Retour en zone → annuler les timers + heartbeat */
    if (!wasInside && isInside) {
      clearTimer(timersRef.current, 'exit');
      clearTimer(timersRef.current, 'gpsLost');
      writeHeartbeat(s.id);
    }

    /* Toujours inside → heartbeat périodique */
    if (isInside) {
      writeHeartbeat(s.id);
    }

    isInsideRef.current = isInside;
  }, [calculateDistance, ask, changeStatus, writeHeartbeat]);

  /* ══════════════════════════════════════════════════════════
     SITUATION 2 — Horaire de fermeture dépassé
     ══════════════════════════════════════════════════════════ */
  const scheduleClosingCheck = useCallback(() => {
    clearTimer(timersRef.current, 'closing');

    const s        = shopRef.current;
    const schedule = scheduleRef.current;
    if (!s || s.status !== 'open' || !schedule) return;

    const dayEntry = schedule[new Date().getDay()]; // 0=dim
    if (!dayEntry?.open) return;

    const now = nowMinutes();

    /* Déjà après l'heure de fermeture → agir tout de suite */
    if (now >= dayEntry.end) {
      /* Grace period de 2 min puis notification */
      setTimer(timersRef.current, 'closing', () => {
        ask(
          '🕐 Votre heure de fermeture est passée',
          `Vos horaires indiquent une fermeture à ${Math.floor(dayEntry.end / 60)}h${String(dayEntry.end % 60).padStart(2,'0')}. Voulez-vous fermer votre magasin ?`,
          'schedule-close',
          [
            { action: 'close-shop', title: 'Fermer maintenant' },
            { action: 'keep-open',  title: 'Rester ouvert encore' },
          ],
          s.id
        );

        /* Toujours pas de réponse après 3 min → fermer automatiquement */
        setTimer(timersRef.current, 'closingForced', () => {
          changeStatus(
            'closed',
            '🔴 Magasin fermé automatiquement',
            'Votre magasin a été marqué comme fermé car vos horaires étaient dépassés.'
          );
        }, 10 * 60 * 1000); // 10 min sans réponse → fermeture automatique

      }, 15 * 60 * 1000); // grace period 15 min

      return;
    }

    /* Programmer la vérification pile à l'heure de fermeture */
    const msUntilClose = (dayEntry.end - now) * 60 * 1000;
    setTimer(timersRef.current, 'closing', () => scheduleClosingCheck(), msUntilClose);

  }, [ask, changeStatus]);

  /* ══════════════════════════════════════════════════════════
     SITUATION 3 — GPS perdu ou refusé
     ══════════════════════════════════════════════════════════ */
  const handleGpsError = useCallback(() => {
    const s = shopRef.current;
    if (!s || s.status !== 'open') return;
    if (s.managedBy === 'schedule') return; // géré par Cloudflare Worker

    remind(
      '📡 Localisation indisponible',
      'Yeldi ne peut plus vérifier si vous êtes dans votre magasin. Votre statut sera mis en attente si le problème persiste.'
    );

    setTimer(timersRef.current, 'gpsLost', () => {
      changeStatus(
        'uncertain',
        '🟡 Statut mis en attente',
        'La localisation est restée indisponible trop longtemps. Vos clients voient votre magasin en statut incertain.'
      );
    }, GPS_LOST_DELAY);
  }, [remind, changeStatus]);

  /* ══════════════════════════════════════════════════════════
     SITUATION 4 — Déconnexion réseau
     ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const onOffline = () => {
      const s = shopRef.current;
      if (!s || s.status !== 'open') return;
      if (s.managedBy === 'schedule') return; // géré par Cloudflare Worker

      remind(
        '📶 Connexion perdue',
        'Yeldi fonctionne hors ligne pour l\'instant. Si la connexion tarde à revenir, le statut de votre magasin sera mis en attente.'
      );

      setTimer(timersRef.current, 'offline', () => {
        /* On essaie d'écrire quand la connexion reviendra */
        const tryUpdate = () => {
          changeStatus(
            'uncertain',
            '🟡 Statut mis en attente',
            'Votre magasin était hors ligne depuis un moment. Vérifiez et mettez à jour votre statut.'
          );
          window.removeEventListener('online', tryUpdate);
        };
        window.addEventListener('online', tryUpdate, { once: true });
      }, OFFLINE_DELAY);
    };

    const onOnline = () => {
      clearTimer(timersRef.current, 'offline');
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online',  onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online',  onOnline);
    };
  }, [remind, changeStatus]);

  /* ══════════════════════════════════════════════════════════
     SITUATION 5 — App en arrière-plan / onglet caché
     ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    let hiddenAt = null;

    const onVisibility = () => {
      const s = shopRef.current;
      if (document.hidden) {
        hiddenAt = Date.now();
      } else {
        if (!hiddenAt || !s || s.status !== 'open') return;
        if (s.managedBy === 'schedule') return; // géré par Cloudflare Worker
        const awayMs = Date.now() - hiddenAt;
        hiddenAt = null;

        /* Plus de 2h en arrière-plan → rappel doux */
        if (awayMs > 2 * 60 * 60 * 1000) {
          remind(
            '👋 Content de vous revoir !',
            'Votre magasin est toujours affiché comme ouvert. Pensez à mettre à jour son statut si besoin.'
          );
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [remind]);

  /* ══════════════════════════════════════════════════════════
     SITUATION 6 — Ouvert depuis trop longtemps (> 12h)
     ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const s = shopRef.current;
    if (!s || s.status !== 'open' || !s.updatedAt) return;
    if (s.managedBy === 'schedule') return; // géré par Cloudflare Worker

    const updatedAt = s.updatedAt?.toDate ? s.updatedAt.toDate() : new Date(s.updatedAt);
    if (isNaN(updatedAt)) return;

    const elapsed   = Date.now() - updatedAt.getTime();
    const remaining = LONG_OPEN_HOURS - elapsed;

    if (remaining <= 0) {
      /* Déjà dépassé → rappel immédiat */
      remind(
        '⏳ Votre magasin est ouvert depuis longtemps',
        'Tout va bien ? Pensez à fermer votre magasin si vous avez terminé votre journée.'
      );
    } else {
      /* Programmer le rappel au moment exact */
      setTimer(timersRef.current, 'longOpen', () => {
        remind(
          '⏳ Votre magasin est ouvert depuis 12h',
          'Tout va bien ? Pensez à fermer votre magasin si vous avez terminé votre journée.'
        );
      }, remaining);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id, shop?.status, shop?.updatedAt, remind]);

  /* ══════════════════════════════════════════════════════════
     DÉMARRAGE — GPS + horaires
     ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    /* ── Mode "schedule" : aucune surveillance GPS/heartbeat ── */
    /* Le Cloudflare Worker gère tout via les horaires             */
    if (shop?.managedBy === 'schedule') {
      stopWatchRef.current?.();
      clearAllTimers(timersRef.current);
      return;
    }

    if (!shop?.latitude || shop?.status !== 'open') {
      stopWatchRef.current?.();
      clearAllTimers(timersRef.current);
      /* Désarmer le onDisconnect si le shop est fermé manuellement */
      if (presenceRef.current && shopRef.current?.id) {
        disarmDisconnect(shopRef.current.id);
        presenceRef.current = null;
      }
      return;
    }

    /* Parser les horaires une fois */
    scheduleRef.current = parseOpenHours(shop.openHours);

    /* Démarrer le GPS */
    stopWatchRef.current?.();
    const stop = startWatching(handlePosition, handleGpsError);
    stopWatchRef.current = stop;

    /* Armer le onDisconnect Firebase dès l'ouverture */
    armDisconnect(shop.id).then((pRef) => {
      presenceRef.current = pRef;
    });

    /* Heartbeat immédiat au démarrage */
    writeHeartbeat(shop.id);

    /* Vérifier les horaires */
    scheduleClosingCheck();

    return () => {
      stopWatchRef.current?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timers = timersRef.current;
      clearAllTimers(timers);
      /* NE PAS appeler disarmDisconnect ici !
         Le cleanup est appelé au démontage du Dashboard (navigation, etc.)
         mais le commerçant peut juste changer de page sans fermer l'app.
         disarmDisconnect est appelé uniquement quand le shop passe à closed/uncertain
         via le bloc if(!shop?.latitude || shop?.status !== 'open') ci-dessus. */
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id, shop?.status, shop?.openHours, shop?.managedBy]);

  return null;
};